// A run's inter-company cost allocation (multi-entity §12.6).
//
// Who bears how much of the employing company's salary cost, and the mirrored
// journal that books it. Shown on the run because that is where the numbers
// are already being read, and because the shares are FROZEN into the run — a
// percentage changed next month does not move what this shows.
//
// Two things it is careful to say out loud:
//   · Pay did not move. The payslips and the statutory filing belong to the
//     employing company; only where the cost is booked changed.
//   · Base = retained + charged, exactly. A reader comparing this against the
//     payroll total should be able to see nothing was created or lost.
//
// It renders nothing when no cost was recharged, which is every run in a group
// that employs everyone where they work.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeftRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { getRunCrossCharge, type RunCrossCharge } from '@/services/payrollSettingsService';

interface Props {
  runId: string;
  /** Only an approved (or later) run has a cross-charge to read. */
  runState: string;
}

const READABLE_STATES = new Set(['approved', 'locked', 'paid']);

const BASIS_LABEL: Record<string, string> = {
  employment_cost: 'salary plus employer contributions',
  salary_only: 'salary only',
};

export const CrossChargeCard: React.FC<Props> = ({ runId, runState }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<RunCrossCharge | null>(null);
  const [showJournal, setShowJournal] = useState(false);

  useEffect(() => {
    if (!READABLE_STATES.has(runState)) return;
    let cancelled = false;
    // A failure here is silence, not an error banner: a tenant that does not
    // use cost allocation should never see a warning about a feature they
    // have not switched on.
    getRunCrossCharge(runId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [runId, runState]);

  const charges = data?.charges ?? [];
  if (!data || charges.length === 0) return null;

  const entries = data.journal?.entries ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <ArrowLeftRight className="h-4 w-4 text-muted-foreground" aria-hidden />
          {t('crossCharge.title', { defaultValue: 'Inter-company cost' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          {t('crossCharge.help', {
            defaultValue:
              '{{employer}} paid this payroll. These companies bear part of the cost, at cost. Nobody’s pay, payslip or statutory filing moved.',
            employer: data.employing_entity_name || t('crossCharge.theEmployer', { defaultValue: 'The employing company' }),
          })}
        </p>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">{t('crossCharge.company', { defaultValue: 'Company' })}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('crossCharge.people', { defaultValue: 'People' })}</th>
                <th scope="col" className="px-3 py-2 text-right font-medium">{t('crossCharge.amount', { defaultValue: 'Amount' })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {charges.map((c) => (
                <tr key={c.legal_entity_id}>
                  <td className="px-3 py-2 text-foreground">{c.entity_name || c.legal_entity_id}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.employees}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-foreground">{c.amount}</td>
                </tr>
              ))}
              <tr className="bg-muted/30">
                <td className="px-3 py-2 text-muted-foreground">
                  {t('crossCharge.retained', {
                    defaultValue: 'Retained by {{employer}}',
                    employer: data.employing_entity_name || t('crossCharge.theEmployer', { defaultValue: 'the employing company' }),
                  })}
                </td>
                <td />
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{data.retained}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">
            {t('crossCharge.base', { defaultValue: 'Total cost {{amount}} {{currency}}', amount: data.base, currency: data.currency })}
          </Badge>
          <span>
            {t('crossCharge.basis', {
              defaultValue: 'Apportioned on {{basis}}',
              basis: BASIS_LABEL[data.basis] ?? data.basis,
            })}
          </span>
          {!data.journal?.balanced && (
            // Should be impossible — the builder refuses to return an
            // unbalanced journal — so if it ever shows, it matters.
            <span className="flex items-center gap-1 text-danger">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              {t('crossCharge.unbalanced', { defaultValue: 'This journal does not balance' })}
            </span>
          )}
        </div>

        {entries.length > 0 && (
          <div>
            <Button size="sm" variant="ghost" onClick={() => setShowJournal((v) => !v)}
              aria-expanded={showJournal}>
              {showJournal
                ? <ChevronUp className="mr-1 h-4 w-4" aria-hidden />
                : <ChevronDown className="mr-1 h-4 w-4" aria-hidden />}
              {t('crossCharge.showJournal', { defaultValue: 'Journal entries' })}
            </Button>
            {showJournal && (
              <div className="mt-2 overflow-x-auto rounded-md border border-border">
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th scope="col" className="px-3 py-2 font-medium">{t('crossCharge.books', { defaultValue: 'In the books of' })}</th>
                      <th scope="col" className="px-3 py-2 font-medium">{t('crossCharge.account', { defaultValue: 'Account' })}</th>
                      <th scope="col" className="px-3 py-2 font-medium">{t('crossCharge.drcr', { defaultValue: 'Dr/Cr' })}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('crossCharge.amount', { defaultValue: 'Amount' })}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {entries.map((e, i) => (
                      <tr key={`${e.logical_code}-${e.legal_entity_id}-${i}`}>
                        <td className="px-3 py-2 text-muted-foreground">
                          {entityName(data, e.legal_entity_id)}
                        </td>
                        <td className="px-3 py-2 text-foreground">
                          {e.account_code} · {e.account}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{e.dr_cr}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">{e.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// entityName resolves a line's company from the charge it belongs to, falling
// back to the id — a line must never render blank, because "in the books of"
// is the column that makes the pair readable.
function entityName(data: RunCrossCharge, id?: string): string {
  if (!id) return '—';
  if (id === data.employing_entity_id) return data.employing_entity_name || id;
  const match = (data.charges ?? []).find((c) => c.legal_entity_id === id);
  return match?.entity_name || id;
}

export default CrossChargeCard;
